import React from 'react';
import { observer } from 'mobx-react-lite';
import { calculatorStore } from '../stores/calculator-store';
import './calculator-ui.scss';

export const CalculatorUI: React.FC = observer(() => {
  return (
    <div className="calculator-ui">
      <div className="calculator-ui__header">
        <h1>Scientific Calculator</h1>
        <p>Advanced calculator with scientific functions</p>
      </div>

      <div className="calculator-ui__container">
        <div className="calculator-ui__main">
          <CalculatorDisplay />
          <CalculatorKeypad />
        </div>
        
        {calculatorStore.isHistoryVisible && (
          <div className="calculator-ui__sidebar">
            <CalculatorHistory />
          </div>
        )}
      </div>
    </div>
  );
});

const CalculatorDisplay: React.FC = observer(() => {
  return (
    <div className="calculator-display">
      <div className="calculator-display__expression">
        {calculatorStore.currentExpression || '\u00A0'}
      </div>
      <div className="calculator-display__result">
        {calculatorStore.currentDisplay}
      </div>
      <div className="calculator-display__mode">
        <span className={`calculator-display__angle ${calculatorStore.currentAngleMode === 'degrees' ? 'active' : ''}`}>
          DEG
        </span>
        <span className={`calculator-display__angle ${calculatorStore.currentAngleMode === 'radians' ? 'active' : ''}`}>
          RAD
        </span>
      </div>
    </div>
  );
});

const CalculatorKeypad: React.FC = observer(() => {
  return (
    <div className="calculator-keypad">
      {/* Row 1: Memory and utility functions */}
      <div className="calculator-keypad__row">
        <CalculatorButton 
          text="MC" 
          className="memory" 
          onClick={() => calculatorStore.memoryClear(0)} 
        />
        <CalculatorButton 
          text="MR" 
          className="memory" 
          onClick={() => calculatorStore.memoryRecall(0)} 
        />
        <CalculatorButton 
          text="M+" 
          className="memory" 
          onClick={() => calculatorStore.memoryAdd(0)} 
        />
        <CalculatorButton 
          text="M-" 
          className="memory" 
          onClick={() => calculatorStore.memorySubtract(0)} 
        />
        <CalculatorButton 
          text="MS" 
          className="memory" 
          onClick={() => calculatorStore.memoryStore(0)} 
        />
      </div>

      {/* Row 2: Scientific functions */}
      <div className="calculator-keypad__row">
        <CalculatorButton 
          text="sin" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('sin')} 
        />
        <CalculatorButton 
          text="cos" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('cos')} 
        />
        <CalculatorButton 
          text="tan" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('tan')} 
        />
        <CalculatorButton 
          text="log" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('log')} 
        />
        <CalculatorButton 
          text="ln" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('ln')} 
        />
      </div>

      {/* Row 3: More scientific functions */}
      <div className="calculator-keypad__row">
        <CalculatorButton 
          text="x²" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('square')} 
        />
        <CalculatorButton 
          text="√" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('sqrt')} 
        />
        <CalculatorButton 
          text="x!" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('factorial')} 
        />
        <CalculatorButton 
          text="1/x" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('reciprocal')} 
        />
        <CalculatorButton 
          text="±" 
          className="function" 
          onClick={() => calculatorStore.applyFunction('negate')} 
        />
      </div>

      {/* Row 4: Clear functions and angle mode */}
      <div className="calculator-keypad__row">
        <CalculatorButton 
          text="AC" 
          className="clear" 
          onClick={() => calculatorStore.clear()} 
        />
        <CalculatorButton 
          text="CE" 
          className="clear" 
          onClick={() => calculatorStore.clearEntry()} 
        />
        <CalculatorButton 
          text="⌫" 
          className="clear" 
          onClick={() => calculatorStore.backspace()} 
        />
        <CalculatorButton 
          text="DEG/RAD" 
          className="mode" 
          onClick={() => calculatorStore.toggleAngleMode()} 
        />
        <CalculatorButton 
          text="÷" 
          className="operator" 
          onClick={() => calculatorStore.inputOperator('÷')} 
        />
      </div>

      {/* Row 5: Numbers and operators */}
      <div className="calculator-keypad__row">
        <CalculatorButton 
          text="7" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('7')} 
        />
        <CalculatorButton 
          text="8" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('8')} 
        />
        <CalculatorButton 
          text="9" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('9')} 
        />
        <CalculatorButton 
          text="×" 
          className="operator" 
          onClick={() => calculatorStore.inputOperator('×')} 
        />
        <CalculatorButton 
          text="(" 
          className="function" 
          onClick={() => calculatorStore.inputNumber('(')} 
        />
      </div>

      {/* Row 6 */}
      <div className="calculator-keypad__row">
        <CalculatorButton 
          text="4" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('4')} 
        />
        <CalculatorButton 
          text="5" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('5')} 
        />
        <CalculatorButton 
          text="6" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('6')} 
        />
        <CalculatorButton 
          text="-" 
          className="operator" 
          onClick={() => calculatorStore.inputOperator('-')} 
        />
        <CalculatorButton 
          text=")" 
          className="function" 
          onClick={() => calculatorStore.inputNumber(')')} 
        />
      </div>

      {/* Row 7 */}
      <div className="calculator-keypad__row">
        <CalculatorButton 
          text="1" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('1')} 
        />
        <CalculatorButton 
          text="2" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('2')} 
        />
        <CalculatorButton 
          text="3" 
          className="number" 
          onClick={() => calculatorStore.inputNumber('3')} 
        />
        <CalculatorButton 
          text="+" 
          className="operator" 
          onClick={() => calculatorStore.inputOperator('+')} 
        />
        <CalculatorButton 
          text="π" 
          className="constant" 
          onClick={() => calculatorStore.inputNumber(Math.PI.toString())} 
        />
      </div>

      {/* Row 8 */}
      <div className="calculator-keypad__row">
        <CalculatorButton 
          text="0" 
          className="number zero" 
          onClick={() => calculatorStore.inputNumber('0')} 
        />
        <CalculatorButton 
          text="." 
          className="number" 
          onClick={() => calculatorStore.inputDecimal()} 
        />
        <CalculatorButton 
          text="=" 
          className="equals" 
          onClick={() => calculatorStore.calculate()} 
        />
        <CalculatorButton 
          text="e" 
          className="constant" 
          onClick={() => calculatorStore.inputNumber(Math.E.toString())} 
        />
        <CalculatorButton 
          text="📈" 
          className="history" 
          onClick={() => calculatorStore.toggleHistory()} 
        />
      </div>
    </div>
  );
});

interface CalculatorButtonProps {
  text: string;
  className?: string;
  onClick: () => void;
}

const CalculatorButton: React.FC<CalculatorButtonProps> = ({ text, className = '', onClick }) => {
  return (
    <button 
      className={`calculator-button ${className}`}
      onClick={onClick}
    >
      {text}
    </button>
  );
};

const CalculatorHistory: React.FC = observer(() => {
  return (
    <div className="calculator-history">
      <div className="calculator-history__header">
        <h3>History</h3>
        <div className="calculator-history__actions">
          <button 
            onClick={() => calculatorStore.clearHistory()}
            className="calculator-history__clear"
          >
            Clear
          </button>
          <button 
            onClick={() => calculatorStore.toggleHistory()}
            className="calculator-history__close"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="calculator-history__list">
        {calculatorStore.calculationHistory.length === 0 ? (
          <div className="calculator-history__empty">
            No calculations yet
          </div>
        ) : (
          calculatorStore.calculationHistory.map(item => (
            <div 
              key={item.id} 
              className="calculator-history__item"
              onClick={() => calculatorStore.useHistoryResult(item.result)}
            >
              <div className="calculator-history__expression">
                {item.expression}
              </div>
              <div className="calculator-history__result">
                = {item.result}
              </div>
              <div className="calculator-history__time">
                {new Date(item.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="calculator-history__memory">
        <h4>Memory Slots</h4>
        {calculatorStore.memorySlots.map((slot, index) => (
          <div key={index} className="calculator-history__memory-slot">
            <span className="calculator-history__memory-label">M{index + 1}:</span>
            <span className="calculator-history__memory-value">
              {slot.value === 0 ? 'Empty' : slot.value.toString()}
            </span>
            {slot.value !== 0 && (
              <button 
                onClick={() => calculatorStore.memoryRecall(index)}
                className="calculator-history__memory-recall"
              >
                Use
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});
